import React, { useState, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  Button,
  Flex,
  Input,
  Loader,
  List,
  Select,
  Tag,
  Text,
  Textarea,
} from '../index';
import { HoneyPod } from '../Form/honeypod';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { companyPositions, startPrace, statusVisit } from '../Form/values';
import { margin, MarginProps } from '../../../components/styles';

import threeMin from '../../../images/banner/three-miniuts.png';

type Props = MarginProps & {
  url?: string;
  id?: string;
  notoption?: boolean; // レセコン確認など
  opening?: boolean; // 開業状況
  visit?: boolean; // 訪問診療可否
  children?: React.ReactNode;
  position?: React.RefObject<any>;
  utmSource?: string; //参照元
  utmMedium?: string; //メディア識別
  utmCampaign?: string; //プロモーションコード
};

const FormItem = styled(Flex)`
  gap: ${({ theme }) => theme.spaces.m};
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;

const FormInput = styled(Flex)`
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.s};
`;

const Label = styled.label`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.fontColors.lightgrey};
`;

const FormBottom = styled(Flex)`
  padding: ${({ theme }) => theme.spaces.xl};
  width: 100%;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.l};

  ${({ theme }) =>
    theme.screen.sp({
      padding: `${theme.spaces.xl} 0`,
    })}
`;

const Links = styled.a`
  display: inline-block;
  padding: ${({ theme }) => theme.spaces.xs} 0;
  transition-duration: ${({ theme }) => theme.transitions.normal};

  &:hover {
    box-shadow: 0 1px 0 ${({ theme }) => theme.borderColors.default};
  }
`;

const Names = styled(Flex)`
  ${({ theme }) =>
    theme.screen.sp({
      flexDirection: 'column',
    })}
`;

const FormList = styled(Flex)`
  flex: 1;
  width: 100%;
  margin: 0 auto;
  padding: ${({ theme }) =>
    `${theme.spaces.xxl} ${theme.spaces.xxl} ${theme.spaces.l}`};
  align-items: flex-start;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.l};
  box-shadow: ${({ theme }) => theme.boxShadow.base};
  border-radius: ${({ theme }) => theme.radii.l};
  background-color: ${({ theme }) => theme.backgroundColors.white};

  ${({ theme }) =>
    theme.screen.sp({
      padding: `${theme.spaces.xxl} ${theme.spaces.l} 0`,
    })}
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.xl};
  background-color: ${({ theme }) => theme.backgroundColors.black};
  padding: ${({ theme }) => `${theme.spaces.xxxl} ${theme.spaces.xxl}`};
  border-radius: ${({ theme }) => theme.radii.m};
`;

const Info = styled(Flex)`
  flex: 1;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spaces.xxl};
`;

const Inner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spaces.xxl};
  max-width: ${({ theme }) => theme.windowSizes.m};
  margin: 0 auto;
  padding: ${({ theme }) => theme.spaces.xxl};

  ${({ theme }) =>
    theme.screen.sp({
      padding: theme.spaces.xxl,
      gridTemplateColumns: '1fr',
    })}
`;

const Root = styled.section<Props>`
  background-color: ${({ theme }) => theme.backgroundColors.grey};
  padding: ${({ theme }) => theme.spaces.xxxl} 0;
  ${margin}
`;

export const FormToppage: React.FC<Props> = (props) => {
  const theme = useTheme();
  const [Submitting, setSubmitting] = useState(false);

  const HandleSubmit = useCallback(() => {
    setSubmitting(true);
  }, []);

  return (
    <Root ref={props.position} id={props.id} mt={props.mt}>
      <Inner>
        <Info>
          <Text as="h3" color="white" size="xxl" fontWeight="bold">
            3分でわかる Dentis ご紹介資料
          </Text>
          <Text as="p" fontWeight="bold" size="m" lineHeight="m" color="white">
            Dentisのサービスご紹介資料をメールでお送りします。
          </Text>
          <Details>
            <img
              src={threeMin}
              width="100%"
              height="auto"
              alt="Dentisカルテ画面イメージ"
            />
            <List color="white">
              <List.Item>業務がシームレスにつながるDentisの特徴</List.Item>
              <List.Item>Dentisの具体的な機能説明</List.Item>
              <List.Item>料金プラン・導入の流れ</List.Item>
            </List>
          </Details>
        </Info>
        <Formik
          initialValues={{
            familyName: '',
            givenName: '',
            companyName: '',
            companyPosition: '',
            tel: '',
            email: '',
            comment: '',
          }}
          onSubmit={(values) => {}}
          validationSchema={Yup.object().shape({
            familyName: Yup.string().required('姓を入力してください'),
            givenName: Yup.string().required('名を入力してください'),
            companyName: Yup.string().required('医療機関名を入力してください'),
            companyPosition: Yup.string().required('役職を選択してください'),
            tel: Yup.number()
              .required('携帯電話番号を入力してください')
              .typeError('半角数字を入力してください'),
            email: Yup.string()
              .email('メールアドレスの形式に誤りがあります。')
              .required('メールアドレスを入力してください'),
          })}
        >
          {({
            // handleSubmit,
            handleChange,
            handleBlur,
            values,
            errors,
            touched,
          }) => (
            <form action={props.url} method="post" onSubmit={HandleSubmit}>
              <FormList>
                <HoneyPod />
                <FormItem>
                  <FormInput>
                    <Input
                      type="hidden"
                      name="utmSource"
                      placeholder="例）google"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={props.utmSource}
                      width={'100%'}
                    />
                    <Input
                      type="hidden"
                      name="utmMedium"
                      placeholder="例）mail"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={props.utmMedium}
                      width={'100%'}
                    />
                    <Input
                      type="hidden"
                      name="utmCampaign"
                      placeholder="例）20240528"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={props.utmCampaign}
                      width={'100%'}
                    />
                  </FormInput>
                </FormItem>
                <Names
                  width="100%"
                  justifyContent="space-between"
                  gap={theme.spaces.l}
                >
                  <FormItem>
                    <Label htmlFor="familyName">
                      姓
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>
                    <FormInput>
                      <Input
                        type="text"
                        name="familyName"
                        placeholder="例）歯科"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.familyName}
                        error={touched.familyName && errors.familyName && true}
                        width={'100%'}
                        required
                      />
                      {touched.familyName && errors.familyName ? (
                        <Text size="s" color="pink" fontWeight="bold">
                          {errors.familyName}
                        </Text>
                      ) : null}
                    </FormInput>
                  </FormItem>

                  <FormItem>
                    <Label htmlFor="givenName">
                      名
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>
                    <FormInput>
                      <Input
                        type="text"
                        name="givenName"
                        placeholder="例）太郎"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.givenName}
                        error={touched.givenName && errors.givenName && true}
                        width={'100%'}
                        required
                      />
                      {touched.givenName && errors.givenName ? (
                        <Text size="s" color="pink" fontWeight="bold">
                          {errors.givenName}
                        </Text>
                      ) : null}
                    </FormInput>
                  </FormItem>
                </Names>
                <FormItem>
                  <Label htmlFor="companyName">
                    医療機関名
                    <Tag color="secondary" nowrap>
                      必須
                    </Tag>
                  </Label>
                  <FormInput>
                    <Input
                      type="text"
                      name="companyName"
                      placeholder="例）メドレー歯科"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.companyName}
                      error={touched.companyName && errors.companyName && true}
                      width={'100%'}
                      required
                    />
                    {touched.companyName && errors.companyName ? (
                      <Text size="s" color="pink" fontWeight="bold">
                        {errors.companyName}
                      </Text>
                    ) : null}
                  </FormInput>
                </FormItem>
                <FormItem>
                  <Label htmlFor="companyPosition">
                    役職
                    <Tag color="secondary" nowrap>
                      必須
                    </Tag>
                  </Label>
                  <FormInput>
                    <Select name="companyPosition" fill required>
                      {companyPositions.map((position) => (
                        <option key={position.value} value={position.value}>
                          {position.label}
                        </option>
                      ))}
                    </Select>
                    {touched.companyPosition && errors.companyPosition ? (
                      <Text size="s" color="pink" fontWeight="bold">
                        {errors.companyPosition}
                      </Text>
                    ) : null}
                  </FormInput>
                </FormItem>
                <FormItem>
                  <Label htmlFor="tel">
                    携帯電話番号
                    <Tag color="secondary" nowrap>
                      必須
                    </Tag>
                  </Label>
                  <FormInput>
                    <Input
                      type="tel"
                      name="tel"
                      placeholder="例）08012345678"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.tel}
                      error={touched.tel && errors.tel && true}
                      width={'100%'}
                      required
                    />
                    {touched.tel && errors.tel ? (
                      <Text size="s" color="pink" fontWeight="bold">
                        {errors.tel}
                      </Text>
                    ) : null}
                  </FormInput>
                </FormItem>
                <FormItem>
                  <Label htmlFor="email">
                    メールアドレス
                    <Tag color="secondary" nowrap>
                      必須
                    </Tag>
                  </Label>
                  <FormInput>
                    <Input
                      type="email"
                      name="email"
                      placeholder="例）info@dentis-cloud.com"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      error={touched.email && errors.email && true}
                      width={'100%'}
                      required
                    />
                    {touched.email && errors.email ? (
                      <Text size="s" color="pink" fontWeight="bold">
                        {errors.email}
                      </Text>
                    ) : null}
                  </FormInput>
                </FormItem>
                {props.opening && (
                  <FormItem>
                    <Label htmlFor="startPrace">
                      開業・分院開業の状況
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>

                    <Select name="startPrace" fill required>
                      {startPrace.map((position) => (
                        <option key={position.value} value={position.value}>
                          {position.label}
                        </option>
                      ))}
                    </Select>
                  </FormItem>
                )}
                {props.visit && (
                  <FormItem>
                    <Label htmlFor="statusVisit">
                      訪問診療の実施状況
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>

                    <Select name="statusVisit" fill required>
                      {statusVisit.map((position) => (
                        <option key={position.value} value={position.value}>
                          {position.label}
                        </option>
                      ))}
                    </Select>
                  </FormItem>
                )}

                <FormBottom>
                  <Text as="p" size="s">
                    「
                    <Links
                      href="https://clinics-cloud.com/privacy"
                      target="_blank"
                    >
                      プライバシーポリシー
                    </Links>
                    」 に同意いただいた上で、送信ボタンを押してください
                  </Text>

                  <Button
                    type="submit"
                    size="l"
                    wide="l"
                    radius="rounded"
                    disabled={Submitting ? true : false}
                  >
                    {Submitting ? (
                      <Flex alignItems="center">
                        <Loader mr={theme.spaces.m} />
                        送信中
                      </Flex>
                    ) : (
                      `送信して資料ダウンロード`
                    )}
                  </Button>
                </FormBottom>
              </FormList>
            </form>
          )}
        </Formik>
      </Inner>
    </Root>
  );
};
